import { useState } from "react";
import { eur, addDays, ESIGIBILITA, MODALITA, CASSE } from "@/lib/api";
import { Field } from "@/components/Common";
import { CassaFields, ScontoFields, CollegataFields } from "@/components/InvoiceExtras";
import { DocumentiPanel, RitenuteFields } from "@/components/InvoiceRefs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DateField } from "@/components/DateField";

const Pick = ({ value, onChange, options, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>{Object.entries(options).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
  </Select>
);

const Rate = ({ f, set }) => {
  const netto = f._netto || 0;
  const rate = f.rate || [];
  const split = (n) => {
    const base = Math.floor((netto / n) * 100) / 100;
    const start = f.scadenza || f.data;
    set("rate", Array.from({ length: n }, (_, i) => ({ scadenza: addDays(start, 30 * i), importo: i === n - 1 ? Math.round((netto - base * (n - 1)) * 100) / 100 : base, pagata: false })));
  };
  const somma = Math.round(rate.reduce((s, r) => s + Number(r.importo || 0), 0) * 100) / 100;
  const upd = (i, k, v) => set("rate", rate.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="sm:col-span-2 space-y-2 rounded-md border border-slate-200 p-3" data-testid="invoice-rate-box">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-medium">Pagamento a rate</span>
        <Select value={String(rate.length || 1)} onValueChange={(v) => (v === "1" ? set("rate", []) : split(Number(v)))}>
          <SelectTrigger data-testid="invoice-rate-count" className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>{[1, 2, 3, 4, 5, 6, 10, 12].map((n) => <SelectItem key={n} value={String(n)}>{n === 1 ? "Unica soluzione" : `${n} rate`}</SelectItem>)}</SelectContent>
        </Select>
        {rate.length > 1 && <span className="text-xs text-slate-500">ogni 30 giorni dalla scadenza, puoi cambiare date e importi</span>}
        {rate.length > 1 && !rate.some((r) => r.pagata) && <Button type="button" size="sm" variant="outline" data-testid="invoice-rate-resplit" onClick={() => split(rate.length)}>Ridividi in parti uguali</Button>}
      </div>
      {rate.map((r, i) => (
        <div key={i} className="grid grid-cols-[2.5rem_1fr_8rem] items-center gap-2 text-sm">
          <span className="text-xs text-slate-500">{i + 1}ª</span>
          <DateField testid={`invoice-rata-date-${i}`} value={r.scadenza} disabled={r.pagata} onChange={(v) => upd(i, "scadenza", v)} />
          <Input data-testid={`invoice-rata-amount-${i}`} type="number" step="0.01" className="text-right font-mono" disabled={r.pagata} value={r.importo} onChange={(e) => upd(i, "importo", e.target.value)} />
        </div>
      ))}
      {rate.length > 1 && <p data-testid="invoice-rate-sum" className={`text-xs ${Math.abs(somma - netto) > 0.009 ? "font-semibold text-rose-700" : "text-emerald-700"}`}>Totale rate {eur(somma)} su netto a pagare {eur(netto)}{Math.abs(somma - netto) > 0.009 ? ": correggi gli importi" : ""}</p>}
    </div>
  );
};

const Pagamento = ({ f, set }) => (
  <div className="grid gap-4 sm:grid-cols-2">
    <Field label="Modalità"><Pick testid="invoice-payment-select" value={f.modalita_pagamento} onChange={(v) => set("modalita_pagamento", v)} options={MODALITA} /></Field>
    <Field label={f.rate?.length > 1 ? "Prima scadenza" : "Scadenza"}><DateField clearable testid="invoice-due-input" value={f.scadenza} onChange={(v) => set("scadenza", v)} /></Field>
    <Field label="IBAN" className="sm:col-span-2"><Input data-testid="invoice-iban-input" className="font-mono" value={f.iban} onChange={(e) => set("iban", e.target.value)} /></Field>
    <Rate f={f} set={set} />
  </div>
);

const Bollo = ({ f, set }) => (
  <label className="flex items-center justify-between gap-4 text-sm">
    <span>Bollo virtuale di 2 € (obbligatorio se le operazioni esenti o non soggette superano 77,47 €)</span>
    <Switch data-testid="invoice-bollo-switch" checked={f.bollo} onCheckedChange={(v) => set("bollo", v)} />
  </label>
);

const tiles = ({ f, set, forfettario, auto, ena, massimale }) => [
  { k: "pagamento", l: "Pagamento", on: true, s: `${MODALITA[f.modalita_pagamento] || ""}${f.rate?.length > 1 ? ` · ${f.rate.length} rate` : ""}`, body: <Pagamento f={f} set={set} /> },
  { k: "esigibilita", l: "Esigibilità IVA", on: f.esigibilita !== "I", s: ESIGIBILITA[f.esigibilita],
    body: <Field label="Esigibilità IVA"><Pick testid="invoice-esigibilita" value={f.esigibilita} onChange={(v) => set("esigibilita", v)} options={ESIGIBILITA} /></Field> },
  { k: "cassa", l: "Cassa previdenziale", on: !!f.cassa.tipo, s: f.cassa.tipo && `${CASSE[f.cassa.tipo] || f.cassa.tipo} ${f.cassa.aliquota}%`,
    body: <CassaFields c={f.cassa} setC={(v) => set("cassa", v)} forfettario={forfettario} /> },
  { k: "ritenute", l: "Ritenute", on: f.ritenute.length > 0, s: f.ritenute.length > 0 && (ena?.massimale > 0 ? `ENASARCO residuo ${eur(ena.residuo)}` : `${f.ritenute.length} impostat${f.ritenute.length > 1 ? "e" : "a"}`),
    body: <div className="space-y-3"><RitenuteFields list={f.ritenute} setList={(v) => set("ritenute", v)} forfettario={forfettario} />
      {ena?.massimale > 0 && <p data-testid="enasarco-residuo" className={`rounded-md px-3 py-2 text-xs ${ena.residuo > 0 ? "bg-sky-50 text-sky-800" : "bg-amber-50 text-amber-800"}`}>
        ENASARCO {f.data.slice(0, 4)} per questo cliente: già soggette {eur(ena.usato)} su massimale {eur(ena.massimale)} · {ena.residuo > 0 ? `residuo ${eur(ena.residuo)}` : "massimale raggiunto: contributo azzerato"}</p>}
      {f.ritenute.some((r) => r.tipo === "RT04") && !massimale && <p data-testid="enasarco-no-massimale" className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">Massimale provvigionale ENASARCO non impostato: indicalo in Impostazioni → ENASARCO per azzerare il contributo oltre il tetto annuo.</p>}</div> },
  { k: "documenti", l: "Documenti correlati", on: f.documenti.length > 0, s: f.documenti.length > 0 && `${f.documenti.length} document${f.documenti.length > 1 ? "i" : "o"}`,
    body: <DocumentiPanel docs={f.documenti} setDocs={(v) => set("documenti", v)} /> },
  { k: "bollo", l: "Dati bollo", on: f.bollo, s: f.bollo && "2,00 €", body: <Bollo f={f} set={set} /> },
  { k: "causale", l: "Causale documento", on: !!f.causale, s: f.causale,
    body: <Field label="Causale"><Textarea data-testid="invoice-causale-input" rows={4} value={f.causale} onChange={(e) => set("causale", e.target.value)} /></Field> },
  { k: "collegata", l: "Fattura principale", on: !!f.collegata_numero || !!f.collegata_id, s: f.collegata_numero,
    body: f.collegata_id ? <p className="text-sm text-slate-600">Documento già collegato a una fattura dell'archivio.</p> : <CollegataFields f={f} set={set} required={auto} /> },
  { k: "riferimento", l: "Rif. amministrazione", on: !!f.riferimento_amministrazione, s: f.riferimento_amministrazione,
    body: <Field label="Riferimento amministrazione (max 20)"><Input data-testid="invoice-riferimento_amministrazione-input" maxLength={20} value={f.riferimento_amministrazione || ""} onChange={(e) => set("riferimento_amministrazione", e.target.value)} /></Field> },
  { k: "sconto", l: "Sconto/magg.", on: !!f.sconto_totale.tipo, s: f.sconto_totale.tipo && (f.sconto_totale.tipo === "SC" ? "Sconto" : "Maggiorazione"),
    body: <ScontoFields s={f.sconto_totale} setS={(v) => set("sconto_totale", v)} /> },
];

export const InvoiceTiles = (props) => {
  const [open, setOpen] = useState(null);
  const list = tiles(props);
  const cur = list.find((t) => t.k === open);
  return (
    <>
      <div data-testid="invoice-tiles" className="grid grid-cols-2 overflow-hidden rounded-lg border border-slate-200 bg-slate-200 sm:grid-cols-3 lg:grid-cols-5" style={{ gap: "1px" }}>
        {list.map((t) => (
          <button key={t.k} type="button" data-testid={`tile-${t.k}`} onClick={() => setOpen(t.k)}
            className="group flex min-h-[4.5rem] flex-col items-center justify-between bg-white px-2 py-2.5 text-center transition-colors hover:bg-sky-50">
            <span className="text-sm font-medium text-sky-700 group-hover:underline">{t.l}</span>
            <span className={`text-[11px] font-bold uppercase tracking-wide ${t.on ? "text-emerald-600" : "text-slate-400"}`} data-testid={`tile-${t.k}-state`}>{t.on ? "Attivo" : "Non attivo"}</span>
            {t.on && t.s && <span className="line-clamp-1 max-w-full text-[11px] text-slate-500">{t.s}</span>}
          </button>
        ))}
      </div>
      <Dialog open={!!cur} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent data-testid="tile-dialog" className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle>{cur?.l}</DialogTitle></DialogHeader>
          {cur?.body}
          <DialogFooter><Button data-testid="tile-dialog-done" onClick={() => setOpen(null)}>Fatto</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
